// CRM 2.0 shared Agreement card — Agreement Preparation, Agreement in Process,
// Agreement Complete (M1-F10, rows M1.19 / M1.41). Drawn as prototype card3: state chip
// (Applied until the letter is eSigned, then Allotted), step chip, ring, Slot Booked,
// Stamp, Sub-Registrar office or "Final Review approved", payment plan line, tracker,
// Rider Details, the bottom message and the ⋮ menu (M1-F11; items from Crm2Page).
import React from "react";
import css from "../ui/Crm2Cards.module.css";
import CardMessage from "../ui/CardMessage";
import CardMenu from "../ui/CardMenu";
import { apptText, Bars, Chip, fmt, leadLabel, lid, planTitle, Ring2, stepDone, unitLine } from "../ui/cardParts";

const AIP = "agreement-in-process";
const DONE = "agreement-complete";
// Prototype S2ORDER: where the booking is in Stage 2.
const S2ORDER = ["letter", "fv", "draft", "otp", "appt", "sdr", "print", "sign", "pickup", "reg", "done"];
const past = (c, step) => S2ORDER.indexOf(c.s2 === "pickup-notdone" ? "pickup" : c.s2) > S2ORDER.indexOf(step);

function YesNo({ ok, when }) {
  return ok ? (
    <span style={{ display: "flex", gap: "3rem" }}>
      <b style={{ color: "#008705", fontWeight: 600 }}>YES</b>
      <span>{when}</span>
    </span>
  ) : (
    <b style={{ color: "#BA0000", fontWeight: 600 }}>NO</b>
  );
}

function tracker(c, tab) {
  if (tab === DONE) return [["Registered", 2], ["Received from Rider", 0], ["Handed to customer", 0]];
  if (tab === AIP) {
    const s = c.s2 === "pickup-notdone" ? "pickup" : c.s2;
    return [
      ["Document Print", past(c, "print") ? 2 : s === "print" ? 1 : 0],
      ["Agreement Sign", past(c, "sign") ? 2 : s === "sign" ? 1 : 0],
      ["Rider Pickup", past(c, "pickup") ? 2 : s === "pickup" ? 1 : 0],
      ["Agreement Done", s === "reg" ? 1 : 0],
    ];
  }
  const cf = c.cfNocStatus && c.cfNocStatus !== "NOT_NEEDED";
  return [
    ["Allotment Letter eSign", past(c, "letter") ? 2 : c.step?.[0] === "Errors" ? 3 : 1],
    ["FarVision Sync", stepDone(c, 2, "FARVISION_SYNC") ? 2 : c.s2 === "fv" ? 1 : 0],
    ...(cf ? [["CF NOC", c.cfNocStatus === "RECEIVED" ? 2 : ["SENT", "PENDING"].includes(c.cfNocStatus) ? 1 : 0]] : []),
    ["Final draft", past(c, "otp") ? 2 : ["draft", "otp"].includes(c.s2) ? 1 : 0],
    ["Appointment", past(c, "appt") ? 2 : c.s2 === "appt" ? 1 : 0],
    ["Stamp", past(c, "sdr") ? 2 : c.s2 === "sdr" ? 1 : 0],
  ];
}

function EmChip({ c }) {
  if (!c.isEmandateEligible || !c.crmEmandateStatus) return null;
  const s = c.crmEmandateStatus;
  return <Chip text={`Emandate ${s}`} tone={s === "Completed" ? "c-ok" : s === "Pending" ? "c-err" : "c-warn"} />;
}

function CfChip({ c }) {
  if (c.cfNocStatus === "RECEIVED") return <Chip text="CF Received" tone="c-ok" />;
  if (c.cfNocStatus === "SENT") return <Chip text="CF Sent" tone="c-warn" />;
  if (c.cfNocStatus === "PENDING") return <Chip text="CF Requested" tone="c-warn" />;
  return null;
}

export default function Crm2AgreementCard({ card: c, tab, menu, onMenu, onAct }) {
  const done = tab === DONE;
  const inAip = tab === AIP;
  const manual = c.enableCreateDraft === false || c.skipCreateDraft === true;
  const letter = c.s2 === "letter";
  const grn = c.stampStatus === "GRN_RECEIVED";
  const grnDate = c.stamp?.grnDate ? fmt(c.stamp.grnDate).split(",")[0] : "";
  const base = Number(c.finalReviewApprovedAt) || 0;
  const ext = base && c.stage2DeadlineAt && c.ring?.total ? Math.round((Number(c.stage2DeadlineAt) - base) / 86400000) - c.ring.total : 0;
  const plan = planTitle(c);
  const appt = apptText(c.appointment);
  return (
    <div className={css.card}>
      <div className={css.cardH}>
        <div className={css.col}>
          <p className={css.u}>{unitLine(c)}</p>
          <div className={css.row}>
            {letter ? <Chip text="Applied" tone="c-pur" dot={false} /> : <Chip text="Allotted" tone="c-ok" dot={false} />}
            {c.step && <Chip text={c.step[1]} tone={c.step[2]} />}
            {ext > 0 && !done && <Chip text={`Extended +${ext} days`} tone="c-warn" />}
            {done && <Chip text="Agreement Done" tone="c-ok" dot={false} />}
            <EmChip c={c} />
            {manual && !done && <Chip text="Create draft off" tone="c-warn" />}
            {!done && !inAip && <CfChip c={c} />}
          </div>
        </div>
        <div className={css.row} style={{ gap: ".4rem", flexWrap: "nowrap" }}>
          {!done && <Ring2 ring={c.ring} apptText={appt} />}
          <CardMenu items={menu} onPick={onMenu} />
        </div>
      </div>
      <div className={css.col} style={{ gap: ".6rem" }}>
        <div className={css.row} style={{ alignItems: "baseline", gap: ".5rem" }}>
          <p className={css.u}>{c.leadName}</p>
          <p className={css.lid}>{lid(c.leadDisplayId)}</p>
        </div>
        <div className={css.row} style={{ justifyContent: "space-between", gap: ".6rem" }}>
          <Chip text={leadLabel(c)} tone={leadLabel(c).startsWith("VJOP") ? "c-err" : "c-lt"} dot={false} />
        </div>
      </div>
      <div className={css.meta}>
        <div className={css.r}>
          <span>VJ SM - {c.smName || "—"}</span>
          <span>VJ CRM - {c.crmName || "—"}</span>
        </div>
        <div className={css.r}>
          <span>{c.displayUnitType} (Display unit type)</span>
        </div>
        {done ? (
          <>
            <div className={css.r}>
              <span>Agreement Date</span>
              <b style={{ color: "#222429", fontWeight: 600 }}>{c.agreementDate ? fmt(c.agreementDate).split(",")[0] : "—"}</b>
            </div>
            <div className={css.r}>
              <span>Registration No.</span>
              <span style={{ color: "#101828" }}>
                {c.agreementNo || "—"}
                {c.appointment?.office ? ` · ${c.appointment.office}` : ""}
              </span>
            </div>
          </>
        ) : (
          <>
            <div className={css.r}>
              <span>{letter ? `Applied On: ${fmt(c.stage1Day0At)}` : `Allotted On: ${fmt(c.allottedAt)}`}</span>
            </div>
            <div className={css.r}>
              <span>Slot Booked</span>
              <YesNo ok={!!c.appointment && c.s2 !== "appt" && ["BOOKED", "DONE", "NOT_DONE"].includes(c.appointment.status)} when={appt} />
            </div>
            <div className={css.r}>
              <span>Stamp</span>
              <YesNo ok={grn} when={grnDate} />
            </div>
            {inAip ? (
              <div className={css.r}>
                <span>Sub-Registrar office</span>
                <span style={{ color: "#101828" }}>{c.appointment?.office || "—"}</span>
              </div>
            ) : (
              <div className={css.r}>
                <span>Final Review approved: {fmt(c.finalReviewApprovedAt)}</span>
              </div>
            )}
          </>
        )}
      </div>
      {plan && <p className={css.plan}>Payment Plan {plan} is selected.</p>}
      <Bars
        steps={tracker(c, tab)}
        title={
          inAip
            ? "Original printed (QR scanned) · signed by VJ's authorised signatory · picked up by the Rider · registration recorded by the Rider"
            : "Allotment letter eSigned · FarVision Application, payout and AMR · final draft confirmed by OTP · appointment booked · stamp duty paid (GRN)"
        }
      />
      {c.riderName && inAip && (
        <div className={css.rider}>
          <div>Rider Details</div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem", textAlign: "right" }}>
            <p style={{ margin: 0 }}>{c.riderName}</p>
          </div>
        </div>
      )}
      <div className={css.cardF}>
        <CardMessage message={c.message} />
        {/* M8-F6 (prototype s2Next "letter"): View Allotment Letter · Send reminder; a letter not made after the last try
            gets M12's Make letter again */}
        {letter && onAct && (
          <span style={{ display: "flex", gap: ".8rem", flex: "none" }}>
            {c.letter?.state === "FAILED" ? (
              <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "makeLetterAgain")}>
                Make letter again
              </button>
            ) : c.letter?.state === "RETRYING" ? null : (
              <>
                <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none", background: "#FFFFFF", color: "#344054", border: "1px solid #D0D5DD" }} onClick={() => onAct(c, "View Allotment Letter")}>
                  View Allotment Letter
                </button>
                <button type="button" className={`${css.btn} ${css.btnCard}`} style={{ flex: "none" }} onClick={() => onAct(c, "alReminder")}>
                  Send reminder
                </button>
              </>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

